import { Component, OnInit, Input, OnDestroy, HostBinding, Optional, Self, ElementRef, DoCheck } from '@angular/core';
import { ControlValueAccessor, Validator, FormBuilder, Validators, FormGroup, NgControl, ValidationErrors, AbstractControl, NgForm, FormGroupDirective } from '@angular/forms';
import { MatFormFieldControl, CanUpdateErrorState, ErrorStateMatcher } from '@angular/material';
import { FocusMonitor } from '@angular/cdk/a11y';
import { Subject } from 'rxjs';
import { untilDestroyed } from 'ngx-take-until-destroy';

import { getErrors } from '@awp/forms';
import { isNullOrEmpty, coerceBooleanProperty } from '@awp/core/utils';
import { _AwpComponentMixinBase } from '../custom-base.component';

export class ExpiryDate {
  constructor(public month: string, public year: string) { }
}

@Component({
  selector: 'ap-expiry-date',
  templateUrl: './expiry-date-input.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: ExpiryDateInputComponent
    }
  ]
})
export class ExpiryDateInputComponent extends _AwpComponentMixinBase implements
  MatFormFieldControl<ExpiryDate>, DoCheck, OnInit, CanUpdateErrorState, ControlValueAccessor, Validator, OnDestroy {

  static nextId = 0;
  @HostBinding() id = `ap-expiry-date-input-${ExpiryDateInputComponent.nextId++}`
  @HostBinding('attr.aria-describedby') describedBy = '';
  controlType = 'ap-expiry-date-input';

  readonly stateChanges: Subject<void> = new Subject<void>();
  focused = false;

  form: FormGroup;
  private _placeholder: string;
  private _required = false;
  private _disabled = false;
  private _value: ExpiryDate = null;

  // tslint:disable-next-line: no-input-rename
  @Input() month: number;
  @Input() year: number;

  private _onTouched = () => { };
  private _onChange = (value: ExpiryDate): void => { };

  constructor(
    @Optional() @Self() ngControl: NgControl,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    private formBuilder: FormBuilder,
    private focusMonitor: FocusMonitor,
    private elRef: ElementRef<HTMLElement>) {

    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.focusMonitor
      .monitor(elRef.nativeElement, true)
      .pipe(untilDestroyed(this))
      .subscribe(origin => {
        this.focused = !!origin;
        this.stateChanges.next();
      });

    this.form = this.formBuilder.group({
        month: [null, [Validators.required, Validators.pattern(/[0-9]{1,2}/)]],
        year: [null, [Validators.required, Validators.pattern(/[0-9]{1,2}/)]],
      },
      {
        updateOn: 'blur'
      });
  }

  ngOnInit() {
    // pad to 2 digits
    this.form.get('month')
      .valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        if (value && value.length === 1) {
          this.form.get('month').setValue(value.padStart(2, '0'));
        }
      });

    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(p => {
        this.notifyChanged(p);
      });
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elRef.nativeElement);
  }

  ngDoCheck(): void {
    if (this._onTouched != null) {
      // if you tab past all date controls, then mark the control as touched
      if ((this.form.get('month').touched && this.form.get('year').touched)) {
        this._onTouched();
      }
    }

    if (this.ngControl != null) {
      // triggers validation
      this.updateErrorState();
    }
  }

  notifyChanged(value: ExpiryDate) {
    this.stateChanges.next();
    this._onChange(value);
    if (this.isExpiryEntryComplete()) {
      this._onTouched();
    }
  }

  //#region ControlValueAccessor
  writeValue(value: ExpiryDate): void {
    if (value != null) {
      this.form.patchValue({
        month: value.month.toString().padStart(2, '0'),
        year: value.year,
      }, { emitEvent: false });
    } else {
      this.form.reset({}, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  //#endregion

  //#region Validator

  validate(control: AbstractControl): ValidationErrors {
    const validationErrors = this.form.valid ? null : getErrors(this.form);
    return validationErrors;
  }

  registerOnValidatorChange?(fn: () => void): void { }

  //#endregion

  // This doesn't seem to be used...
  private isError(value: ExpiryDate) {
    if (isNullOrEmpty(value.month) && isNullOrEmpty(value.year)) {
      return this.required;
    }

    if (!/[0-9]{1,2}/.test(value.month)) return true;
    if (!/[0-9]{2}/.test(value.year)) return true;

    return false;
  }

  get empty(): boolean {
    return !this.form.value || (isNullOrEmpty(this.form.value.month) && isNullOrEmpty(this.form.value.year));
  }

  get value() {
    const { value: { month, year }} = this.form;
    if (!isNullOrEmpty(month) && !isNullOrEmpty(year)) {
      return new ExpiryDate(month, year);
    }
    return null;
  }

  set value(value: ExpiryDate) {
    const { month, year } = value || new ExpiryDate(null, null);
    this.form.setValue({month, year});
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.form.disable() : this.form.enable();
    this.stateChanges.next();
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  setValue() {
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elRef.nativeElement.querySelector('input').focus();
    }
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  private isExpiryEntryComplete(): boolean {
    return (this.form.get('month').touched || this.form.get('month').value)
        && (this.form.get('year').touched || this.form.get('year').value)
  }

}
